import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Button,
  Typography,
  Divider,
  IconButton,
  alpha,
  Stack,
} from "@mui/material";
import { Close, Save, LocalHospital } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import useClientDetails from "../../../../../hooks/useClientDetails";
import { QK } from "../../../../../base/qk";
import { getVisitByClientID } from "../../../../../api/services/scheduling";
import {
  createMedication,
  getMedicineNames,
} from "../../../../../api/services/medication";
import { medicationValidationSchema } from "../Medication.yup.config";
import MedicationBasicInfo from "./Medications/MedicationBasicInfo";
import MedicationScheduleInfo from "./Medications/MedicationScheduleInfo";
import MedicationInventorySection from "./Medications/MedicationInventorySection";
import MedicationTimingSection from "./Medications/MedicationTimingSection";

const initialFormData = {
  title: "",
  medicine_type: "Blister Pack",
  controlled_medication: false,
  medication_type: "",
  route_administration: "",
  frequency: "",
  start_date: "",
  end_date: null,
  expiry_date: null,
  how_assisted: "",
  every: null,
  notes: "",
  reason_for_medication: "",
  number_of_cards: 0,
  slots_per_card: 0,
  loose_slots: 0,
  total_slots_remaining: 0,
};

const AddMedicationModal = ({ open, handleClose }) => {
  const { id: clientID } = useParams();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { fullName } = useClientDetails(clientID);

  const [formData, setFormData] = useState(initialFormData);
  const [individualMeds, setIndividualMeds] = useState([
    { name: "", dose: "", initial_stock_qty: "" },
  ]);
  const [times, setTimes] = useState([{ time: "", visit: null }]);
  const [selectedMedications, setSelectedMedications] = useState([]);
  const [selectedDays, setSelectedDays] = useState([]);
  const [errors, setErrors] = useState({});
  const [search, setSearch] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setIsSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    if (formData.medicine_type === "Blister Pack") {
      const cards = parseInt(formData.number_of_cards) || 0;
      const slots = parseInt(formData.slots_per_card) || 0;
      const loose = parseInt(formData.loose_slots) || 0;
      setFormData((prev) => ({
        ...prev,
        total_slots_remaining: cards * slots + loose,
      }));
    }
  }, [
    formData.number_of_cards,
    formData.slots_per_card,
    formData.loose_slots,
    formData.medicine_type,
  ]);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleIndMedChange = (index, field, value) => {
    const updated = [...individualMeds];
    updated[index][field] = value;
    setIndividualMeds(updated);
  };

  const handleTimeChange = (index, field, value) => {
    let actualValue = value;
    if (value && typeof value === "object" && value.target) {
      actualValue = value.target.value;
    }
    const updated = [...times];
    updated[index][field] = actualValue;
    setTimes(updated);
  };

  const handleDaySelection = (day) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const { data: visits } = useQuery([QK.visits, clientID], () =>
    getVisitByClientID(clientID)
  );

  const { data: medicineNames, isLoading: isSearchingMedicines } = useQuery(
    [QK.medicineNames, search],
    () => getMedicineNames(search),
    { enabled: search.length >= 2 }
  );

  const validate = async () => {
    try {
      const normalizedTimes = times.map((t) => {
        let timeVal = t.time;
        if (timeVal && typeof timeVal === "object" && timeVal.target) {
          timeVal = timeVal.target.value;
        }
        return {
          time: timeVal || "",
          visit: t.visit?.value || t.visit?.id || t.visit || null,
        };
      });

      const payload = {
        ...formData,
        expiry_date:
          formData.expiry_date && formData.expiry_date !== ""
            ? formData.expiry_date
            : null,
        every:
          formData.every && formData.every !== "" ? Number(formData.every) : null,
        individual_meds: individualMeds,
        med_times: normalizedTimes,
        weekdays: selectedDays,
        medicine_list: selectedMedications.map((m) => m.value),
      };
      await medicationValidationSchema.validate(payload, { abortEarly: false });
      setErrors({});
      return { valid: true, errors: {} };
    } catch (err) {
      const newErrors = {};
      if (err && err.inner && Array.isArray(err.inner)) {
        err.inner.forEach((e) => {
          newErrors[e.path] = e.message;
        });
      } else if (err && err.message) {
        newErrors._global = err.message;
      }
      setErrors(newErrors);
      return { valid: false, errors: newErrors };
    }
  };

  const onSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const result = await validate();

    if (!result.valid) {
      setIsSubmitting(false);
      return;
    }

    const normalizedTimes = times.map((t) => {
      let timeVal = t.time;
      if (timeVal && typeof timeVal === "object" && timeVal.target) {
        timeVal = timeVal.target.value;
      }
      return {
        time: timeVal || "",
        visit: t.visit?.value || t.visit?.id || t.visit || null,
      };
    });

    const finalData = {
      ...formData,
      client: clientID,
      expiry_date:
        formData.expiry_date && formData.expiry_date !== ""
          ? formData.expiry_date
          : null,
      every:
        formData.every && formData.every !== "" ? Number(formData.every) : null,
      weekdays: selectedDays,
      individual_meds:
        formData.medicine_type === "Individual Medication"
          ? individualMeds.map((m) => ({
              ...m,
              initial_stock_qty: Number(m.initial_stock_qty) || 0,
            }))
          : [],
      medicine_list:
        formData.medicine_type === "Blister Pack"
          ? selectedMedications.map((m) => m.value)
          : [],
      med_times: normalizedTimes,
    };

    try {
      const response = await createMedication(finalData);
      if (response && (response.status === 201 || response.status === 200)) {
        enqueueSnackbar("Medication Saved Successfully", {
          variant: "success",
        });
        queryClient.invalidateQueries([QK.clientMedications, clientID]);
        handleModalClose();
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.message ||
          "Submission failed",
        { variant: "error" }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    setFormData(initialFormData);
    setIndividualMeds([{ name: "", dose: "", initial_stock_qty: "" }]);
    setTimes([{ time: "", visit: null }]);
    setSelectedMedications([]);
    setSelectedDays([]);
    setErrors({});
    setSearch("");
    setIsSubmitting(false);
    handleClose();
  };

  const visitOptions =
    visits?.map((v) => ({ value: v.id, label: v.visit_name })) || [];

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      BackdropProps={{ sx: { backgroundColor: "rgba(0, 0, 0, 0.6)" } }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", sm: 920, md: 1020 },
          bgcolor: "#ffffff",
          boxShadow: "0 24px 80px rgba(0,0,0,0.25)",
          borderRadius: 3,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: { xs: 2, sm: 3, md: 4 },
            py: 2.5,
            borderBottom: "2px solid #f1f5f9",
            flexShrink: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: alpha("#2469F0", 0.08),
                color: "#2469F0",
                p: 1,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 40,
                height: 40,
              }}
            >
              <LocalHospital sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography
                sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.05rem" }}
              >
                Add Medication Setup
              </Typography>
              <Typography
                sx={{ fontSize: "0.78rem", color: "#64748b", mt: 0.25 }}
              >
                For:{" "}
                <Box
                  component="span"
                  sx={{ fontWeight: 600, color: "#0f172a" }}
                >
                  {fullName}
                </Box>
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={handleModalClose}
            sx={{ bgcolor: "#f1f5f9", "&:hover": { bgcolor: "#e2e8f0" } }}
          >
            <Close />
          </IconButton>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: { xs: 2, sm: 3, md: 4 },
            py: 3,
          }}
        >
          <MedicationBasicInfo
            formData={formData}
            handleChange={handleChange}
            errors={errors}
          />

          <Divider sx={{ my: 3 }} />

          <MedicationScheduleInfo
            formData={formData}
            handleChange={handleChange}
            errors={errors}
          />

          <MedicationInventorySection
            formData={formData}
            handleChange={handleChange}
            individualMeds={individualMeds}
            handleIndMedChange={handleIndMedChange}
            setIndividualMeds={setIndividualMeds}
            selectedMedications={selectedMedications}
            setSelectedMedications={setSelectedMedications}
            medicineNames={medicineNames}
            isSearchingMedicines={isSearchingMedicines}
            errors={errors}
            search={search}
            setSearch={setSearch}
          />

          <MedicationTimingSection
            formData={formData}
            handleChange={handleChange}
            times={times}
            handleTimeChange={handleTimeChange}
            setTimes={setTimes}
            selectedDays={selectedDays}
            handleDaySelection={handleDaySelection}
            visitOptions={visitOptions}
            errors={errors}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            px: { xs: 2, sm: 3, md: 4 },
            py: 2.5,
            borderTop: "2px solid #f1f5f9",
            bgcolor: "#ffffff",
            flexShrink: 0,
          }}
        >
          <Button
            type="button"
            onClick={handleModalClose}
            variant="outlined"
            disabled={isSubmitting}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.1,
              fontWeight: 600,
              textTransform: "none",
              borderColor: "#e2e8f0",
              color: "#64748b",
              "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f8fafc" },
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onSubmit}
            disabled={isSubmitting}
            variant="contained"
            startIcon={<Save sx={{ fontSize: 18 }} />}
            sx={{
              px: 4,
              py: 1.1,
              borderRadius: 2,
              bgcolor: "#2469F0",
              fontWeight: 600,
              textTransform: "none",
              fontSize: "0.85rem",
              "&:hover": {
                bgcolor: "#1A5BD4",
                boxShadow: "0 4px 16px rgba(36, 105, 240, 0.3)",
              },
              "&.Mui-disabled": {
                bgcolor: "#93C5FD",
                color: "#ffffff",
              },
            }}
          >
            {isSubmitting ? "Saving..." : "Save Medication"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddMedicationModal;